import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    dashboardApi,
    LeadHygieneResponse,
    LeadHygieneBuffer,
    LeadHygieneSupplier,
    LeadCleaningLogRow
} from '../../services/dashboardApi';
import {
    ShieldCheck,
    ShieldAlert,
    RefreshCw,
    Search,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    Database,
    Activity,
    Clock,
    Filter
} from 'lucide-react';

const GEO_FLAGS: Record<string, string> = {
    se: '🇸🇪 Sweden',
    no: '🇳🇴 Norway',
    fi: '🇫🇮 Finland',
    de: '🇩🇪 Germany'
};

const SUPPLIER_NAMES: Record<string, string> = {
    nl: 'NordicLeads',
    eg: 'eGentic',
    lp: 'LunaPark',
    lo: 'LeadOne'
};

const DEBOUNCE_CODES: Record<string, { label: string; badgeClass: string }> = {
    '1': { label: 'Syntax Error (1)', badgeClass: 'bg-red-100 text-red-800 border-red-200' },
    '2': { label: 'Spam Trap (2)', badgeClass: 'bg-red-200 text-red-900 border-red-300 font-bold' },
    '3': { label: 'Disposable (3)', badgeClass: 'bg-amber-100 text-amber-800 border-amber-200' },
    '4': { label: 'Accept-All (4)', badgeClass: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    '5': { label: 'Deliverable (5)', badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200 font-medium' },
    '6': { label: 'Invalid (6)', badgeClass: 'bg-red-100 text-red-800 border-red-200' },
    '7': { label: 'Unknown (7)', badgeClass: 'bg-gray-100 text-gray-700 border-gray-200' },
    '8': { label: 'Role-Based (8)', badgeClass: 'bg-purple-100 text-purple-800 border-purple-200' }
};

export default function LeadHygieneView() {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [data, setData] = useState<LeadHygieneResponse | null>(null);
    const [error, setError] = useState('');
    const [timeframeDays, setTimeframeDays] = useState<number>(7);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGeo, setSelectedGeo] = useState<string>('all');
    const [selectedCode, setSelectedCode] = useState<string>('all');

    const fetchData = useCallback(async (isManualRefresh = false) => {
        try {
            if (isManualRefresh) setRefreshing(true);
            else setLoading(true);
            setError('');
            const raw: any = await dashboardApi.getLeadHygiene(timeframeDays);
            const res: LeadHygieneResponse = raw?.body ? (typeof raw.body === 'string' ? JSON.parse(raw.body) : raw.body) : raw;
            setData(res);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch lead hygiene data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [timeframeDays]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filteredLogs = useMemo(() => {
        if (!data?.recent_logs) return [];
        return data.recent_logs.filter((log: LeadCleaningLogRow) => {
            const matchesSearch =
                !searchTerm ||
                log.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (log.supplier && log.supplier.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (log.result && log.result.toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesGeo = selectedGeo === 'all' || log.geo === selectedGeo;
            const matchesCode = selectedCode === 'all' || log.code === selectedCode;

            return matchesSearch && matchesGeo && matchesCode;
        });
    }, [data?.recent_logs, searchTerm, selectedGeo, selectedCode]);

    const kpis = data?.kpis || {
        total_tested: 0,
        deliverable: 0,
        spam_traps: 0,
        invalids: 0,
        disposables: 0,
        accept_all: 0,
        pass_rate_pct: 0
    };

    const buffers = data?.buffers || {};

    return (
        <div className="space-y-6">
            {/* Header & Controls */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-200/80 shadow-sm">
                <div>
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
                            <ShieldCheck size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                DeBounce Hygiene & Buffer Health
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Automated nightly pre-cleaner (01:30 UTC) maintaining 2,500 deliverable leads per geo
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Timeframe selector */}
                    <div className="flex items-center bg-gray-100 p-1 rounded-xl text-xs font-medium text-gray-600">
                        {[
                            { label: '24 Hours', days: 1 },
                            { label: '7 Days', days: 7 },
                            { label: '30 Days', days: 30 }
                        ].map((btn) => (
                            <button
                                key={btn.days}
                                onClick={() => setTimeframeDays(btn.days)}
                                className={`px-3 py-1.5 rounded-lg transition-all ${
                                    timeframeDays === btn.days
                                        ? 'bg-white text-indigo-700 font-semibold shadow-xs'
                                        : 'hover:text-gray-900'
                                }`}
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>

                    {/* Refresh Button */}
                    <button
                        onClick={() => fetchData(true)}
                        disabled={loading || refreshing}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-xl text-xs font-medium transition-colors shadow-2xs cursor-pointer disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={refreshing ? 'animate-spin text-indigo-600' : ''} />
                        <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl text-sm flex items-center gap-2">
                    <AlertTriangle size={18} className="text-red-600 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {/* Architecture Info Banner */}
            <div className="bg-gradient-to-r from-blue-50/70 via-indigo-50/50 to-emerald-50/70 border border-blue-200/80 rounded-2xl p-4.5 shadow-2xs">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 text-blue-700 rounded-xl mt-0.5">
                        <Activity size={18} />
                    </div>
                    <div className="text-xs text-gray-700 leading-relaxed space-y-1">
                        <div className="font-semibold text-gray-900 text-sm">
                            Decoupled Pre-Cleaning Architecture Active
                        </div>
                        <p>
                            Pre-cleaning runs at <strong>01:30 UTC</strong> via <code className="px-1.5 py-0.5 bg-white/80 rounded border border-blue-200 font-mono text-[11px]">DailyLeadBufferCleaner-0100</code> to ensure each geo table always maintains at least <strong>2,500</strong> deliverable (<code className="font-mono text-[11px]">Code 5</code>) incubator graduates (joined &gt;30d ago).
                        </p>
                        <p className="text-gray-600">
                            Because leads are pre-cleaned ahead of time, morning recruit intake at <strong>04:00 UTC</strong> completes instantly in under 90 seconds across all accounts without Lambda timeouts or EventBridge retries.
                        </p>
                    </div>
                </div>
            </div>

            {/* 1. Geo Buffer Health Cards */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                        <Database size={16} className="text-indigo-600" />
                        <span>Pre-Cleaned Lead Buffer Status (Target: 2,500 per geo)</span>
                    </h3>
                    {data?.timestamp && (
                        <div className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock size={12} />
                            <span>Audited: {data.timestamp}</span>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Sweden */}
                    {buffers.se && (
                        <div className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-sm hover:border-indigo-200 transition-all">
                            <div className="flex items-start justify-between">
                                <div>
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                        Sweden Pool
                                    </span>
                                    <div className="text-lg font-bold text-gray-900 flex items-center gap-2 mt-0.5">
                                        <span>🇸🇪 Sverige Post & Svenska Lån</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Shared table: <code className="font-mono text-[11px]">se.subscribers</code></p>
                                </div>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                    buffers.se.status === 'healthy'
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                        : 'bg-amber-50 text-amber-700 border-amber-200'
                                }`}>
                                    {buffers.se.status === 'healthy' ? '✓ Healthy' : '⚠ Deficit'}
                                </span>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 flex items-baseline justify-between">
                                <div>
                                    <div className="text-2xl font-extrabold text-gray-900">
                                        {buffers.se.current_buffer.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        Pre-cleaned valid leads ready
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-emerald-600">
                                        {buffers.se.pct_of_target}%
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        of {buffers.se.target_buffer.toLocaleString()} target
                                    </div>
                                </div>
                            </div>

                            <div className="w-full bg-gray-100 h-2 rounded-full mt-3 overflow-hidden">
                                <div
                                    className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                                    style={{ width: `${Math.min(buffers.se.pct_of_target, 100)}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Norway */}
                    {buffers.no && (
                        <div className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-sm hover:border-indigo-200 transition-all">
                            <div className="flex items-start justify-between">
                                <div>
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                        Norway Pool
                                    </span>
                                    <div className="text-lg font-bold text-gray-900 flex items-center gap-2 mt-0.5">
                                        <span>🇳🇴 Norske Tilbud</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Dedicated table: <code className="font-mono text-[11px]">no.subscribers</code></p>
                                </div>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                    buffers.no.status === 'healthy'
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                        : 'bg-amber-50 text-amber-700 border-amber-200'
                                }`}>
                                    {buffers.no.status === 'healthy' ? '✓ Healthy' : '⚠ Deficit'}
                                </span>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 flex items-baseline justify-between">
                                <div>
                                    <div className="text-2xl font-extrabold text-gray-900">
                                        {buffers.no.current_buffer.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        Pre-cleaned valid leads ready
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-emerald-600">
                                        {buffers.no.pct_of_target}%
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        of {buffers.no.target_buffer.toLocaleString()} target
                                    </div>
                                </div>
                            </div>

                            <div className="w-full bg-gray-100 h-2 rounded-full mt-3 overflow-hidden">
                                <div
                                    className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                                    style={{ width: `${Math.min(buffers.no.pct_of_target, 100)}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Finland */}
                    {buffers.fi && (
                        <div className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-sm hover:border-indigo-200 transition-all">
                            <div className="flex items-start justify-between">
                                <div>
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                        Finland Pool
                                    </span>
                                    <div className="text-lg font-bold text-gray-900 flex items-center gap-2 mt-0.5">
                                        <span>🇫🇮 Suomalaisetlainat</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Dedicated table: <code className="font-mono text-[11px]">fi.subscribers</code></p>
                                </div>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                    buffers.fi.status === 'healthy'
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                        : 'bg-amber-50 text-amber-700 border-amber-200'
                                }`}>
                                    {buffers.fi.status === 'healthy' ? '✓ Healthy' : '⚠ Deficit'}
                                </span>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100 flex items-baseline justify-between">
                                <div>
                                    <div className="text-2xl font-extrabold text-gray-900">
                                        {buffers.fi.current_buffer.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        Pre-cleaned valid leads ready
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-emerald-600">
                                        {buffers.fi.pct_of_target}%
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        of {buffers.fi.target_buffer.toLocaleString()} target
                                    </div>
                                </div>
                            </div>

                            <div className="w-full bg-gray-100 h-2 rounded-full mt-3 overflow-hidden">
                                <div
                                    className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                                    style={{ width: `${Math.min(buffers.fi.pct_of_target, 100)}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 2. DeBounce Quality KPIs */}
            <div>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3">
                    Cleaning Quality & Safety Metrics ({timeframeDays === 1 ? 'Last 24 Hours' : `Last ${timeframeDays} Days`})
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {/* Total Tested */}
                    <div className="bg-white p-4 rounded-xl border border-gray-200/80 shadow-xs">
                        <div className="text-xs font-medium text-gray-500">Total Leads Audited</div>
                        <div className="text-2xl font-bold text-gray-900 mt-1">
                            {kpis.total_tested.toLocaleString()}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1">
                            DeBounce API calls
                        </div>
                    </div>

                    {/* Deliverable (Code 5) */}
                    <div className="bg-white p-4 rounded-xl border border-emerald-200 bg-emerald-50/20 shadow-xs">
                        <div className="text-xs font-semibold text-emerald-800 flex items-center justify-between">
                            <span>Deliverable (Code 5)</span>
                            <CheckCircle2 size={14} className="text-emerald-600" />
                        </div>
                        <div className="text-2xl font-bold text-emerald-700 mt-1">
                            {kpis.deliverable.toLocaleString()}
                        </div>
                        <div className="text-[11px] text-emerald-600 font-medium mt-1">
                            {kpis.pass_rate_pct}% Pass Rate
                        </div>
                    </div>

                    {/* Spam Traps (Code 2) - Highlighted */}
                    <div className={`p-4 rounded-xl border shadow-xs ${
                        kpis.spam_traps > 0
                            ? 'bg-red-100 border-red-300 ring-2 ring-red-400/50'
                            : 'bg-white border-gray-200/80'
                    }`}>
                        <div className="text-xs font-bold text-red-800 flex items-center justify-between">
                            <span>Spam Traps (Code 2)</span>
                            <ShieldAlert size={16} className="text-red-600" />
                        </div>
                        <div className={`text-2xl font-extrabold mt-1 ${kpis.spam_traps > 0 ? 'text-red-700' : 'text-gray-900'}`}>
                            {kpis.spam_traps.toLocaleString()}
                        </div>
                        <div className={`text-[11px] mt-1 ${kpis.spam_traps > 0 ? 'text-red-800 font-semibold' : 'text-gray-400'}`}>
                            {kpis.spam_traps > 0 ? '🚨 Blocked from intake' : '0 detected'}
                        </div>
                    </div>

                    {/* Invalids (Code 6) */}
                    <div className="bg-white p-4 rounded-xl border border-gray-200/80 shadow-xs">
                        <div className="text-xs font-medium text-gray-500 flex items-center justify-between">
                            <span>Invalid Emails (Code 6)</span>
                            <XCircle size={14} className="text-red-500" />
                        </div>
                        <div className="text-2xl font-bold text-gray-900 mt-1">
                            {kpis.invalids.toLocaleString()}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1">
                            Hard-bounces avoided
                        </div>
                    </div>

                    {/* Disposables & Accept-All */}
                    <div className="bg-white p-4 rounded-xl border border-gray-200/80 shadow-xs">
                        <div className="text-xs font-medium text-gray-500">Disposable & Accept-All</div>
                        <div className="text-2xl font-bold text-gray-900 mt-1">
                            {(kpis.disposables + kpis.accept_all).toLocaleString()}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1">
                            {kpis.disposables} disp / {kpis.accept_all} all
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. Supplier Quality Breakdown */}
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h3 className="font-bold text-gray-900 text-sm">
                            Supplier Cleaning & Deliverability Breakdown
                        </h3>
                        <p className="text-xs text-gray-500">
                            Evaluates quality, spam traps, and pass rates by lead origin
                        </p>
                    </div>
                </div>

                {data?.suppliers && data.suppliers.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-gray-600">
                            <thead className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                <tr>
                                    <th className="py-3 px-4">Geo</th>
                                    <th className="py-3 px-4">Supplier</th>
                                    <th className="py-3 px-4 text-right">Tested</th>
                                    <th className="py-3 px-4 text-right">Deliverable</th>
                                    <th className="py-3 px-4 text-right">Spam Traps</th>
                                    <th className="py-3 px-4 text-right">Invalids</th>
                                    <th className="py-3 px-4 text-right">Pass Rate</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.suppliers.map((sup: LeadHygieneSupplier, idx: number) => (
                                    <tr key={`${sup.geo}-${sup.supplier}-${idx}`} className="hover:bg-gray-50/60 transition-colors">
                                        <td className="py-3 px-4 font-medium text-gray-900">
                                            {GEO_FLAGS[sup.geo] || sup.geo.toUpperCase()}
                                        </td>
                                        <td className="py-3 px-4 font-semibold text-gray-800">
                                            {SUPPLIER_NAMES[sup.supplier] || sup.supplier}
                                        </td>
                                        <td className="py-3 px-4 text-right font-medium text-gray-900">
                                            {sup.total.toLocaleString()}
                                        </td>
                                        <td className="py-3 px-4 text-right text-emerald-600 font-semibold">
                                            {sup.deliverable.toLocaleString()}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            {sup.spam_traps > 0 ? (
                                                <span className="px-2 py-0.5 bg-red-100 text-red-800 border border-red-200 rounded-md font-bold text-xs">
                                                    {sup.spam_traps}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">0</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right text-gray-600">
                                            {sup.invalids.toLocaleString()}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${
                                                sup.pass_rate_pct >= 90
                                                    ? 'bg-emerald-100 text-emerald-800'
                                                    : sup.pass_rate_pct >= 75
                                                    ? 'bg-amber-100 text-amber-800'
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {sup.pass_rate_pct}%
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-8 text-center text-gray-500">
                        <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2 opacity-80" />
                        <p className="text-sm font-medium text-gray-700">No verifications needed in selected timeframe</p>
                        <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                            The clean buffer in Sweden (47k+), Norway (42k+), and Finland (4k+) fully exceeds the 2,500 lead threshold. New DeBounce API calls only trigger when a geo buffer dips below 2,500.
                        </p>
                    </div>
                )}
            </div>

            {/* 4. Recent Lead Verifications Table */}
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h3 className="font-bold text-gray-900 text-sm">
                            Recent DeBounce Lead Verifications
                        </h3>
                        <p className="text-xs text-gray-500">
                            Immutable audit records logged in <code className="font-mono text-[11px]">admin.lead_cleaning_logs</code>
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Search */}
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search email or supplier..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                            />
                        </div>

                        {/* Geo filter */}
                        <select
                            value={selectedGeo}
                            onChange={(e) => setSelectedGeo(e.target.value)}
                            className="py-1.5 px-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        >
                            <option value="all">All GEOs</option>
                            <option value="se">🇸🇪 SE</option>
                            <option value="no">🇳🇴 NO</option>
                            <option value="fi">🇫🇮 FI</option>
                        </select>

                        {/* Code filter */}
                        <select
                            value={selectedCode}
                            onChange={(e) => setSelectedCode(e.target.value)}
                            className="py-1.5 px-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        >
                            <option value="all">All Codes</option>
                            <option value="5">Code 5 (Deliverable)</option>
                            <option value="2">Code 2 (Spam Trap)</option>
                            <option value="6">Code 6 (Invalid)</option>
                            <option value="3">Code 3 (Disposable)</option>
                        </select>
                    </div>
                </div>

                {filteredLogs.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs text-gray-600">
                            <thead className="bg-gray-50/80 uppercase tracking-wider font-semibold text-gray-500 border-b border-gray-100">
                                <tr>
                                    <th className="py-2.5 px-4">Email</th>
                                    <th className="py-2.5 px-4">Geo</th>
                                    <th className="py-2.5 px-4">Supplier</th>
                                    <th className="py-2.5 px-4">Result / Code</th>
                                    <th className="py-2.5 px-4">Diagnostic Reason</th>
                                    <th className="py-2.5 px-4 text-center">Deliverable</th>
                                    <th className="py-2.5 px-4 text-right">Checked At (UTC)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredLogs.map((log: LeadCleaningLogRow, idx: number) => {
                                    const codeConfig = DEBOUNCE_CODES[log.code] || {
                                        label: `${log.result} (${log.code})`,
                                        badgeClass: 'bg-gray-100 text-gray-700 border-gray-200'
                                    };

                                    return (
                                        <tr key={`${log.email}-${idx}`} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="py-2.5 px-4 font-mono font-medium text-gray-800">
                                                {log.email}
                                            </td>
                                            <td className="py-2.5 px-4 uppercase font-semibold text-gray-700">
                                                {GEO_FLAGS[log.geo] ? GEO_FLAGS[log.geo].split(' ')[0] + ' ' + log.geo.toUpperCase() : log.geo}
                                            </td>
                                            <td className="py-2.5 px-4 font-medium text-gray-700">
                                                {SUPPLIER_NAMES[log.supplier] || log.supplier}
                                            </td>
                                            <td className="py-2.5 px-4">
                                                <span className={`px-2 py-0.5 rounded-md border text-[11px] ${codeConfig.badgeClass}`}>
                                                    {codeConfig.label}
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-4 text-gray-500">
                                                {log.reason || '—'}
                                            </td>
                                            <td className="py-2.5 px-4 text-center">
                                                {log.is_deliverable ? (
                                                    <span className="text-emerald-600 font-bold">✓ YES</span>
                                                ) : (
                                                    <span className="text-red-500 font-bold">✗ NO</span>
                                                )}
                                            </td>
                                            <td className="py-2.5 px-4 text-right font-mono text-gray-500">
                                                {log.created_at}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-8 text-center text-gray-400 text-xs">
                        <Filter size={24} className="mx-auto text-gray-300 mb-2" />
                        <span>No verification audit entries matching the current filter.</span>
                    </div>
                )}
            </div>
        </div>
    );
}
